import type { VoidFunctionComponent } from 'react';

import { useOnConfirm } from '../../../../../contexts/DialogContext';
import { useAddToast } from '../../../../../contexts/ToastContext';
import useDeleteTeam from '../../../../../hooks/api/teams/useDeleteTeam';
import { getErrorMessage } from '../../../../../ReactQuery/errors';
import TeamTableRowActions from '../shared/TableRowActions';

type Props = {
  teamId: number;
  teamName: string;
};

/**
 * Actions which appear on each row in the teams table,
 * allowing the user to edit, delete, or view the team.
 *
 * @param {Props} props - The props for the component.
 * @param {number} props.teamId - The ID of the team.
 * @param {() => void} props.onEditTeam - The function to call when the user clicks the edit button.
 * @returns {JSX.Element} The component.
 */
const TeamsTableRowActions: VoidFunctionComponent<Props> = ({
  teamId,
  teamName,
}) => {
  const onConfirm = useOnConfirm();
  const addToast = useAddToast();
  const { mutateAsync: deleteTeam, isLoading: isDeleting } = useDeleteTeam(teamId);

  //  Function to delete a team, and handle any errors
  const onDelete = async () => {
    try {
      await deleteTeam();
      addToast({
        type: 'success',
        content: `Team "${teamName}" deleted.`,
      });
    } catch (error: unknown) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, `Failed to delete team "${teamName}".`),
      });
    }
  };

  //  Wraps the above function in a confirm dialog
  const handleDelete = () =>
    onConfirm({
      title: `Delete "${teamName}"?`,
      body: `Users who are not part of any other team may no longer be able to access any projects.`,
      onConfirm: onDelete,
      confirmText: 'Delete',
      confirmBtnType: 'error',
    });

  return (
    <TeamTableRowActions
      teamId={teamId}
      toEdit={`/account/settings/organization/teams/${teamId}`}
      onDelete={handleDelete}
      isDeleting={isDeleting}
    />
  );
};

export default TeamsTableRowActions;
